import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiArrowLeft,
  FiRefreshCw,
  FiTrash2,
  FiGlobe,
  FiEyeOff,
  FiMessageSquare,
  FiFileText,
  FiUsers
} from 'react-icons/fi';
import './AdminManagementPage.css';

const AdminManagementPage = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('feedback');
  const [searchText, setSearchText] = useState('');
  const [feedbacks, setFeedbacks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [publicTemplates, setPublicTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actioningKey, setActioningKey] = useState('');

  const handleSessionExpired = useCallback(() => {
    toast.dismiss();
    localStorage.removeItem('userInfo');
    setUserInfo(null);
    setTimeout(() => {
      navigate('/login', {
        state: {
          message: '会话已过期，请重新登录'
        }
      });
    }, 1000);
  }, [navigate]);

  const loadAdminData = useCallback(async (currentUserInfo) => {
    if (!currentUserInfo?.userName) {
      return;
    }

    setIsLoading(true);

    try {
      const authResponse = await fetch(`/api/admin/access/${encodeURIComponent(currentUserInfo.userName)}`);

      if (authResponse.status === 401) {
        handleSessionExpired();
        return;
      }

      if (!authResponse.ok) {
        toast.error('仅管理员可访问管理后台');
        navigate('/dashboard');
        return;
      }

      const nextUserInfo = {
        ...currentUserInfo,
        isAdmin: true
      };

      setUserInfo(nextUserInfo);
      localStorage.setItem('userInfo', JSON.stringify(nextUserInfo));

      const [feedbackResponse, templateResponse, publicTemplateResponse] = await Promise.all([
        fetch(`/api/admin/feedbacks/${encodeURIComponent(currentUserInfo.userName)}`),
        fetch(`/api/admin/templates/${encodeURIComponent(currentUserInfo.userName)}`),
        fetch(`/api/admin/public-templates/${encodeURIComponent(currentUserInfo.userName)}`)
      ]);

      const responses = [feedbackResponse, templateResponse, publicTemplateResponse];

      if (responses.some(response => response.status === 401)) {
        handleSessionExpired();
        return;
      }

      if (responses.some(response => response.status === 403)) {
        toast.error('当前账号没有管理权限');
        navigate('/dashboard');
        return;
      }

      const [feedbackResult, templateResult, publicTemplateResult] = await Promise.all([
        feedbackResponse.json(),
        templateResponse.json(),
        publicTemplateResponse.json()
      ]);

      if (feedbackResult.success) {
        setFeedbacks(feedbackResult.feedbacks || []);
      } else {
        toast.error(feedbackResult.message || '获取意见建议失败');
      }

      if (templateResult.success) {
        setTemplates(templateResult.templates || []);
      } else {
        toast.error(templateResult.message || '获取个人模板失败');
      }

      if (publicTemplateResult.success) {
        setPublicTemplates(publicTemplateResult.templates || []);
      } else {
        toast.error(publicTemplateResult.message || '获取公共模板失败');
      }
    } catch (error) {
      console.error('加载管理数据失败:', error);
      toast.error('加载管理数据失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  }, [handleSessionExpired, navigate]);

  useEffect(() => {
    const savedUserInfo = localStorage.getItem('userInfo');

    if (!savedUserInfo) {
      navigate('/login');
      return;
    }

    const parsedUserInfo = JSON.parse(savedUserInfo);
    setUserInfo(parsedUserInfo);
    loadAdminData(parsedUserInfo);
  }, [navigate, loadAdminData]);

  const refreshData = async () => {
    if (!userInfo?.userName) {
      return;
    }

    await loadAdminData(userInfo);
  };

  const handleDeleteFeedback = async (feedbackItem) => {
    if (!userInfo?.userName) {
      return;
    }

    if (!window.confirm(`确定删除来自“${feedbackItem.name || feedbackItem.username}”的这条意见建议吗？`)) {
      return;
    }

    const actionKey = `feedback-${feedbackItem.id}`;
    setActioningKey(actionKey);

    try {
      const response = await fetch(`/api/admin/feedbacks/${encodeURIComponent(userInfo.userName)}/${feedbackItem.id}`, {
        method: 'DELETE'
      });

      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      const result = await response.json();

      if (result.success) {
        toast.success(result.message || '意见建议删除成功');
        setFeedbacks(prev => prev.filter(item => item.id !== feedbackItem.id));
      } else {
        toast.error(result.message || '删除失败');
      }
    } catch (error) {
      console.error('删除意见建议失败:', error);
      toast.error('删除失败，请稍后重试');
    } finally {
      setActioningKey('');
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!userInfo?.userName) {
      return;
    }

    if (!window.confirm(`确定删除模板“${template.name}”吗？`)) {
      return;
    }

    const actionKey = `template-delete-${template.owner}-${template.id}`;
    setActioningKey(actionKey);

    try {
      const response = await fetch(
        `/api/admin/templates/${encodeURIComponent(userInfo.userName)}/${encodeURIComponent(template.owner)}/${template.id}`,
        {
          method: 'DELETE'
        }
      );

      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      const result = await response.json();

      if (result.success) {
        toast.success(result.message || '模板删除成功');
        setTemplates(prev => prev.filter(item => !(item.owner === template.owner && item.id === template.id)));
        setPublicTemplates(prev => prev.filter(item => !(item.owner === template.owner && item.id === template.id)));
      } else {
        toast.error(result.message || '删除失败');
      }
    } catch (error) {
      console.error('删除模板失败:', error);
      toast.error('删除失败，请稍后重试');
    } finally {
      setActioningKey('');
    }
  };

  const handleToggleTemplateShare = async (template, nextShared) => {
    if (!userInfo?.userName) {
      return;
    }

    const actionKey = `template-share-${template.owner}-${template.id}`;
    setActioningKey(actionKey);

    try {
      const response = await fetch(
        `/api/admin/templates/${encodeURIComponent(userInfo.userName)}/${encodeURIComponent(template.owner)}/${template.id}/share`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            isShared: nextShared
          })
        }
      );

      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      const result = await response.json();

      if (result.success) {
        toast.success(result.message || '模板状态更新成功');
        await loadAdminData(userInfo);
      } else {
        toast.error(result.message || '更新失败');
      }
    } catch (error) {
      console.error('更新模板公开状态失败:', error);
      toast.error('更新失败，请稍后重试');
    } finally {
      setActioningKey('');
    }
  };

  const normalizedSearchText = searchText.trim().toLowerCase();

  const filteredFeedbacks = useMemo(() => {
    if (!normalizedSearchText) {
      return feedbacks;
    }

    return feedbacks.filter(item => {
      return [
        item.type,
        item.content,
        item.name,
        item.username,
        item.createdAtText
      ].some(value => String(value || '').toLowerCase().includes(normalizedSearchText));
    });
  }, [feedbacks, normalizedSearchText]);

  const filteredTemplates = useMemo(() => {
    if (!normalizedSearchText) {
      return templates;
    }

    return templates.filter(item => {
      return [
        item.name,
        item.owner,
        item.workTitle,
        item.workArea,
        item.workContent,
        item.safetyMeasures,
        item.remarks
      ].some(value => String(value || '').toLowerCase().includes(normalizedSearchText));
    });
  }, [templates, normalizedSearchText]);

  const filteredPublicTemplates = useMemo(() => {
    if (!normalizedSearchText) {
      return publicTemplates;
    }

    return publicTemplates.filter(item => {
      return [
        item.name,
        item.owner,
        item.author,
        item.workTitle,
        item.workArea,
        item.workContent,
        item.safetyMeasures,
        item.remarks
      ].some(value => String(value || '').toLowerCase().includes(normalizedSearchText));
    });
  }, [publicTemplates, normalizedSearchText]);

  const currentList = activeTab === 'feedback'
    ? filteredFeedbacks
    : activeTab === 'templates'
      ? filteredTemplates
      : filteredPublicTemplates;

  return (
    <div className="admin-page">
      <div className="admin-page-shell">
        <div className="admin-topbar">
          <button className="admin-topbar-btn" onClick={() => navigate('/dashboard')}>
            <FiArrowLeft />
            <span>返回台账</span>
          </button>
          <div className="admin-topbar-center">
            <h1>管理后台</h1>
            <p>{userInfo?.name || userInfo?.userName || '管理员'}</p>
          </div>
          <button className="admin-topbar-btn primary" onClick={refreshData} disabled={isLoading}>
            <FiRefreshCw />
            <span>{isLoading ? '加载中...' : '刷新数据'}</span>
          </button>
        </div>

        <div className="admin-summary-grid">
          <div className="admin-summary-card">
            <div className="admin-summary-icon feedback">
              <FiMessageSquare />
            </div>
            <div>
              <div className="admin-summary-value">{feedbacks.length}</div>
              <div className="admin-summary-label">意见建议</div>
            </div>
          </div>
          <div className="admin-summary-card">
            <div className="admin-summary-icon templates">
              <FiFileText />
            </div>
            <div>
              <div className="admin-summary-value">{templates.length}</div>
              <div className="admin-summary-label">个人模板</div>
            </div>
          </div>
          <div className="admin-summary-card">
            <div className="admin-summary-icon public">
              <FiUsers />
            </div>
            <div>
              <div className="admin-summary-value">{publicTemplates.length}</div>
              <div className="admin-summary-label">公共模板</div>
            </div>
          </div>
        </div>

        <div className="admin-panel">
          <div className="admin-panel-header">
            <div className="admin-tab-group">
              <button
                className={`admin-tab-btn ${activeTab === 'feedback' ? 'active' : ''}`}
                onClick={() => setActiveTab('feedback')}
              >
                意见建议
              </button>
              <button
                className={`admin-tab-btn ${activeTab === 'templates' ? 'active' : ''}`}
                onClick={() => setActiveTab('templates')}
              >
                个人模板
              </button>
              <button
                className={`admin-tab-btn ${activeTab === 'public' ? 'active' : ''}`}
                onClick={() => setActiveTab('public')}
              >
                公共模板
              </button>
            </div>
            <input
              className="admin-search-input"
              type="text"
              placeholder={activeTab === 'feedback' ? '搜索意见建议内容、类型、提交人' : '搜索模板名称、归属人、内容'}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="admin-empty-state">正在加载管理数据...</div>
          ) : currentList.length === 0 ? (
            <div className="admin-empty-state">当前没有匹配的数据</div>
          ) : (
            <div className="admin-card-list">
              {activeTab === 'feedback' && filteredFeedbacks.map(item => (
                <div key={item.id} className="admin-card">
                  <div className="admin-card-header">
                    <div className="admin-card-title-group">
                      <span className="admin-badge">{item.type}</span>
                      <h3>{item.name || item.username}</h3>
                    </div>
                    <button
                      className="admin-manage-btn danger"
                      onClick={() => handleDeleteFeedback(item)}
                      disabled={actioningKey === `feedback-${item.id}`}
                    >
                      <FiTrash2 />
                      <span>{actioningKey === `feedback-${item.id}` ? '处理中...' : '删除'}</span>
                    </button>
                  </div>
                  <div className="admin-meta-row">
                    <span>账号：{item.username}</span>
                    <span>{item.createdAtText || item.createdAt}</span>
                  </div>
                  <div className="admin-content-block">{item.content}</div>
                </div>
              ))}

              {activeTab === 'templates' && filteredTemplates.map(item => (
                <div key={`${item.owner}-${item.id}`} className="admin-card">
                  <div className="admin-card-header">
                    <div className="admin-card-title-group">
                      <span className={`admin-badge ${item.isShared ? 'shared' : ''}`}>
                        {item.isShared ? '已公开' : '未公开'}
                      </span>
                      <h3>{item.name}</h3>
                    </div>
                    <div className="admin-action-row">
                      <button
                        className="admin-manage-btn"
                        onClick={() => handleToggleTemplateShare(item, !item.isShared)}
                        disabled={actioningKey === `template-share-${item.owner}-${item.id}`}
                      >
                        {item.isShared ? <FiEyeOff /> : <FiGlobe />}
                        <span>
                          {actioningKey === `template-share-${item.owner}-${item.id}`
                            ? '处理中...'
                            : item.isShared
                              ? '取消公开'
                              : '设为公开'}
                        </span>
                      </button>
                      <button
                        className="admin-manage-btn danger"
                        onClick={() => handleDeleteTemplate(item)}
                        disabled={actioningKey === `template-delete-${item.owner}-${item.id}`}
                      >
                        <FiTrash2 />
                        <span>{actioningKey === `template-delete-${item.owner}-${item.id}` ? '处理中...' : '删除'}</span>
                      </button>
                    </div>
                  </div>
                  <div className="admin-meta-row">
                    <span>归属账号：{item.owner}</span>
                    <span>更新时间：{item.updatedAt || item.createdAt}</span>
                  </div>
                  <div className="admin-detail-grid">
                    <div><strong>作业标题：</strong>{item.workTitle}</div>
                    <div><strong>作业区域：</strong>{item.workArea}</div>
                    <div><strong>作业内容：</strong>{item.workContent}</div>
                    <div><strong>安全措施：</strong>{item.safetyMeasures}</div>
                    <div><strong>备注：</strong>{item.remarks}</div>
                  </div>
                </div>
              ))}

              {activeTab === 'public' && filteredPublicTemplates.map(item => (
                <div key={`${item.owner}-${item.id}`} className="admin-card">
                  <div className="admin-card-header">
                    <div className="admin-card-title-group">
                      <span className="admin-badge shared">公共模板</span>
                      <h3>{item.name}</h3>
                    </div>
                    <div className="admin-action-row">
                      <button
                        className="admin-manage-btn"
                        onClick={() => handleToggleTemplateShare(item, false)}
                        disabled={actioningKey === `template-share-${item.owner}-${item.id}`}
                      >
                        <FiEyeOff />
                        <span>{actioningKey === `template-share-${item.owner}-${item.id}` ? '处理中...' : '取消公开'}</span>
                      </button>
                      <button
                        className="admin-manage-btn danger"
                        onClick={() => handleDeleteTemplate(item)}
                        disabled={actioningKey === `template-delete-${item.owner}-${item.id}`}
                      >
                        <FiTrash2 />
                        <span>{actioningKey === `template-delete-${item.owner}-${item.id}` ? '处理中...' : '删除模板'}</span>
                      </button>
                    </div>
                  </div>
                  <div className="admin-meta-row">
                    <span>归属账号：{item.owner}</span>
                    <span>公开展示：{item.author || item.owner}</span>
                  </div>
                  <div className="admin-detail-grid">
                    <div><strong>作业标题：</strong>{item.workTitle}</div>
                    <div><strong>作业区域：</strong>{item.workArea}</div>
                    <div><strong>作业内容：</strong>{item.workContent}</div>
                    <div><strong>安全措施：</strong>{item.safetyMeasures}</div>
                    <div><strong>备注：</strong>{item.remarks}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminManagementPage;
